import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { saveData } from 'services/api/saveData';

interface ReleaseEmailPreviewProps {
    htmlContent: string;
    sendMailButton?: {
        showButton: boolean;
        apiUrl: string;
        sendTestMail?: boolean;
    }
}

const ReleaseEmailPreview: React.FC<ReleaseEmailPreviewProps> = ({
    htmlContent, sendMailButton,
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { revealModal, closeAllModals, closeModal } = useModal();
    const [buttonLoader, setButtonLoader] = useState(false);
    const shadowRootRef = useRef<HTMLDivElement>(null);
    
    // Create the shadow root
    useEffect(() => {
        if (shadowRootRef.current) {
            // Only create a new shadow root if it not yet exist
            if (!shadowRootRef.current.shadowRoot) {
                const shadowRoot = shadowRootRef.current.attachShadow({ mode: 'open' });
                shadowRoot.innerHTML = htmlContent;

                revealModal();
            } else {
                // If a shadow root already exists, just update it
                shadowRootRef.current.shadowRoot.innerHTML = htmlContent;

                revealModal();
            }
        }
    }, []);

    // Handle sending of the email
    const handleSendMail = async (mailType: 'test' | 'official') => {
        if (sendMailButton) {
            try {
                setButtonLoader(true);

                // Call the backend to send the release email
                const response = await saveData({ 
                    apiUrl: sendMailButton.apiUrl, 
                    method: 'post',
                    data: mailType === 'test' ? { test_mail: true } : {}
                });

                // Handle successful response
                if (response?.status === 200) {
                    setFloatingAlert({ 'type': 'success' });
                    setButtonLoader(false);
                    
                    // After sending an official mail, close the whole modal stack
                    if (mailType === 'official') {
                        closeAllModals();
                    }
                }
            } catch (error: any) {
                setFloatingAlert({ type: 'danger', message: 'validation.general.save_failed' })
                setButtonLoader(false);
            }
        }
    };

    // Handles the closing of the modal
    const handleClose = () => {
        setButtonLoader(false);
        closeModal();
    };

    return (
        <div className='email-preview-modal'>
            <div className='header'>
                <h4>
                    {t('email.email_layout.example_title')}
                </h4>
            </div>
            <div style={{ overflow: 'hidden', transformOrigin: 'top left' }}>
                <div style={{ boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)' }} ref={shadowRootRef} />
            </div>
            <div className='footer'>
                <div className='left-content'></div>
                <div className='right-content'>
                    <SecondaryButton
                        onClick={handleClose} 
                        label="general.close"
                        size='small'
                    />
                    {sendMailButton?.sendTestMail && (
                        <SecondaryButton
                            onClick={() => handleSendMail('test')}
                            label="email.general.send_test_email"
                            size='small'
                        />
                    )}
                    {sendMailButton?.showButton && (
                        <PrimaryButton
                            onClick={() => handleSendMail('official')}
                            label="email.general.send_email"
                            size='small'
                            loading={buttonLoader}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReleaseEmailPreview;